import React from "react";
import {
    useReactTable,
    ColumnDef,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
    SortingState
} from "@tanstack/react-table";
import { Dashboard as DashboardType } from "@/hooks/dashboard";
import { Utils } from "@/common/utils";
import PencilSimpleIcon from "Components/Icons/PencilSimpleIcon";
import ThrashIcon from "Components/Icons/ThrashIcon";
import { MoreVertical } from "lucide-react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { DashboardQueries } from "@/common/dashboards";
import { useSchoolSettings } from "@/hooks/schools";
import ArrowsUpDown from "Components/Icons/ArrowsUpDown";
import { ArrowDown } from "lucide-react";

interface TanDashboardListProps {
    cards: DashboardType[];
    onClickEdit?: (dashboard: DashboardType) => void;
    onClickDelete?: (dashboardName: string, dashboardCategory?: string) => void;
    onClickFavorite?: (dashboardId: number) => void;
    checkIfIsFavorite: (dashboardId: number) => boolean;
}

const getCategoryColorClass = (category: string) => {
    const normalized = category.trim().toLowerCase();
    const hash = Array.from(normalized).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = hash % 6;
    return `cat-color-${colorIndex}`;
};

// Reusable sort header component
const SortableHeader = ({
    columnId,
    label,
    sorting,
    setSorting
}: {
    columnId: string;
    label: string;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
}) => {
    const sort = sorting.find(s => s.id === columnId);
    return (
        <button
            type="button"
            aria-label={`Filter content in the table by ${label}`}
            style={{
                background: "none",
                outline: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                minWidth: 110,
                font: "inherit",
                color: "inherit",
                whiteSpace: "nowrap"
            }}
            tabIndex={0}
            onClick={e => {
                e.stopPropagation();
                setSorting(curr => {
                    const currSort = curr.find(s => s.id === columnId);
                    if (!currSort) return [{ id: columnId, desc: false }];
                    if (!currSort.desc) return [{ id: columnId, desc: true }];
                    return [];
                });
                e.currentTarget.focus();
            }}
            onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSorting(curr => {
                        const currSort = curr.find(s => s.id === columnId);
                        if (!currSort) return [{ id: columnId, desc: false }];
                        if (!currSort.desc) return [{ id: columnId, desc: true }];
                        return [];
                    });
                }
            }}
            onFocus={e => e.stopPropagation()}
            className="sortable-header-btn"
        >
            <span style={{ whiteSpace: "nowrap" }}>{label}</span>
            {sort ? (
                <ArrowDown size={18} stroke="#5C5C5C" style={{ marginLeft: "2.7px", transform: sort.desc ? "rotate(180deg)" : undefined }} />
            ) : (
                <ArrowsUpDown size={32} style={{ paddingTop: "8px" }} />
            )}
        </button>
    );
};

export const TanDashboardList: React.FC<TanDashboardListProps> = ({
    cards,
    onClickEdit,
    onClickDelete,
    onClickFavorite,
    checkIfIsFavorite
}) => {
    const navigate = useNavigate();
    const { data: schoolSettings } = useSchoolSettings();
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [openDropdownId, setOpenDropdownId] = React.useState<number | null>(null);

    const getDashboardUrl = (dashboardName: string) => {
        if (DashboardQueries[dashboardName] && schoolSettings) {
            return `${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(dashboardName)}${DashboardQueries[dashboardName]?.replace(/60/g, schoolSettings.at_risk_threshold)}`;
        }
        return `${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(dashboardName)}`;
    };

    const columns = React.useMemo<ColumnDef<DashboardType>[]>(
        () => [
            {
                header: () => <SortableHeader columnId="name" label="Dashboard Name" sorting={sorting} setSorting={setSorting} />,
                accessorKey: "name",
                enableSorting: true,
                cell: info => {
                    const name = info.row.original.name || "";
                    const shortName = name.length > 45 ? name.slice(0, 45) + "…" : name;
                    return <span title={name}>{shortName}</span>;
                }
            },
            {
                header: () => <SortableHeader columnId="category" label="Group Name" sorting={sorting} setSorting={setSorting} />,
                accessorKey: "category",
                enableSorting: true,
                cell: info => (
                    <span
                        className={`dash-list-cat ${getCategoryColorClass(info.row.original.category ?? "")}`}
                    >
                        {info.row.original.category}
                    </span>
                )
            },
            {
                header: () => <SortableHeader columnId="description" label="Description" sorting={sorting} setSorting={setSorting} />,
                accessorKey: "description",
                enableSorting: true,
                cell: info => {
                    const raw = info.row.original.description || "";
                    const text = raw.replace(/<[^>]+>/g, "");
                    const short = text.length > 70 ? text.slice(0, 70) + "…" : text;
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span className="dashboard-list-description" title={text} style={{ flex: 2 }}>{short}</span>
                            {info.row.original.dashboard_type === "BYODashboard" && Utils.hasAdminAccess() && (
                                <Dropdown
                                    align="end"
                                    className="dashboard-card-dropdown"
                                    show={openDropdownId === info.row.original.id}
                                    onToggle={(nextShow, meta) => {
                                        if (meta.source === "select") return;
                                        setOpenDropdownId(nextShow ? info.row.original.id : null);
                                    }}
                                    onClick={e => e.stopPropagation()}
                                    style={{ cursor: "pointer" }}
                                >
                                    <Dropdown.Toggle
                                        as="button"
                                        className="dropdown-toggle-no-style"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setOpenDropdownId(openDropdownId === info.row.original.id ? null : info.row.original.id);
                                        }}
                                        onMouseDown={e => e.stopPropagation()}
                                        style={{ border: "none", background: "none", padding: 0 }}
                                    >
                                        <MoreVertical size={18} />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={e => { e.stopPropagation(); onClickEdit?.(info.row.original); setOpenDropdownId(null); }}>
                                            <span style={{ marginRight: 8 }}><PencilSimpleIcon /></span> Edit
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={e => { e.stopPropagation(); onClickDelete?.(info.row.original.name, info.row.original.category); setOpenDropdownId(null); }}>
                                            <span style={{ marginRight: 8 }}><ThrashIcon /></span> Delete
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                            <div
                                className="icon-star-favorite-container"
                                onClick={e => { e.stopPropagation(); onClickFavorite?.(info.row.original.id); }}
                                role="button"
                                tabIndex={0}
                                aria-pressed={checkIfIsFavorite(info.row.original.id)}
                                aria-label={checkIfIsFavorite(info.row.original.id) ? "Remove from favorites" : "Add to favorites"}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onClickFavorite?.(info.row.original.id);
                                    }
                                }}
                            >
                                <span className={`icon-star-favorite${checkIfIsFavorite(info.row.original.id) ? " favorite" : ""}`}>
                                    <i
                                        className={`type icon icon-star${checkIfIsFavorite(info.row.original.id) ? " favorite" : ""}`}
                                        aria-hidden="true"
                                    ></i>
                                </span>
                            </div>
                        </div>
                    );
                }
            }
        ],
        [onClickEdit, onClickDelete, onClickFavorite, checkIfIsFavorite, sorting, setSorting, openDropdownId]
    );

    const table = useReactTable({
        data: cards,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
        onSortingChange: setSorting
    });

    return (
        <div className="dashboard-list-view">
            <table className="table table-bordered table-hover table-dashboard">
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    style={{ textAlign: "left", verticalAlign: "middle" }}
                                    aria-sort={
                                        header.column.getIsSorted() === "asc" ? "ascending" :
                                        header.column.getIsSorted() === "desc" ? "descending" :
                                        "none"
                                    }
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr
                            key={row.id}
                            style={{ cursor: "pointer" }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Go to dashboard ${row.original.name}`}
                            onClick={e => {
                                const tag = (e.target as HTMLElement).tagName;
                                if (tag !== "BUTTON" && tag !== "A" && tag !== "INPUT" && tag !== "SELECT" && tag !== "TEXTAREA") {
                                    navigate(getDashboardUrl(row.original.name));
                                }
                            }}
                            onKeyDown={e => {
                                if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                                    navigate(getDashboardUrl(row.original.name));
                                }
                            }}
                        >
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} onClick={cell.column.id !== "description" ? undefined : (e) => e.stopPropagation()}>
                                    {cell.column.id === "description" ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <span className="dashboard-list-description" title={row.original.description?.replace(/<[^>]+>/g, "") || ""} style={{ flex: 2 }}>
                                                {(row.original.description || "").replace(/<[^>]+>/g, "").slice(0, 70)}{(row.original.description || "").replace(/<[^>]+>/g, "").length > 70 ? "…" : ""}
                                            </span>
                                            {row.original.dashboard_type === "BYODashboard" && Utils.hasAdminAccess() && (
                                                <Dropdown
                                                    align="end"
                                                    className="dashboard-card-dropdown"
                                                    show={openDropdownId === row.original.id}
                                                    onToggle={(nextShow, meta) => {
                                                        if (meta.source === "select") return;
                                                        setOpenDropdownId(nextShow ? row.original.id : null);
                                                    }}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <Dropdown.Toggle
                                                        as="button"
                                                        className="dropdown-toggle-no-style"
                                                        onClick={e => { e.stopPropagation(); e.preventDefault(); setOpenDropdownId(openDropdownId === row.original.id ? null : row.original.id); }}
                                                        onMouseDown={e => e.stopPropagation()}
                                                        style={{ border: "none", background: "none", padding: 0 }}
                                                        tabIndex={0}
                                                    >
                                                        <MoreVertical size={18} />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={e => { e.stopPropagation(); onClickEdit?.(row.original); setOpenDropdownId(null); }} tabIndex={0}>
                                                            <span style={{ marginRight: 8 }}><PencilSimpleIcon /></span> Edit
                                                        </Dropdown.Item>
                                                        <Dropdown.Item onClick={e => { e.stopPropagation(); onClickDelete?.(row.original.name, row.original.category); setOpenDropdownId(null); }} tabIndex={0}>
                                                            <span style={{ marginRight: 8 }}><ThrashIcon /></span> Delete
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}
                                            <div
                                                className="icon-star-favorite-container"
                                                onClick={e => { e.stopPropagation(); onClickFavorite?.(row.original.id); }}
                                                role="button"
                                                tabIndex={0}
                                                aria-pressed={checkIfIsFavorite(row.original.id)}
                                                aria-label={checkIfIsFavorite(row.original.id) ? "Remove from favorites" : "Add to favorites"}
                                                onKeyDown={e => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        onClickFavorite?.(row.original.id);
                                                    }
                                                }}
                                                onFocus={e => e.stopPropagation()}
                                            >
                                                <span className={`icon-star-favorite${checkIfIsFavorite(row.original.id) ? " favorite" : ""}`}>
                                                    <i
                                                        className={`type icon icon-star${checkIfIsFavorite(row.original.id) ? " favorite" : ""}`}
                                                        aria-hidden="true"
                                                    ></i>
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        flexRender(cell.column.columnDef.cell, cell.getContext())
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};